import React, { useState, useRef, useEffect } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";

function getDaysAgo(date) {
  if (!date) return Infinity;
  const now = new Date();
  const d = new Date(date);
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
}
function isThisWeek(date) {
  return getDaysAgo(date) < 7;
}
function isLast30ButNotThisWeek(date) {
  const days = getDaysAgo(date);
  return days >= 7 && days < 30;
}
function isOlderThan30(date) {
  return getDaysAgo(date) >= 30;
}

function ThreadMenu({ threadId, onDelete, onRequestRename, disabled, menuOpen, setMenuOpen }) {
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (typeof menuOpen === "boolean" && setMenuOpen) {
      setMenuOpen(!!anchorEl);
    }
    // eslint-disable-next-line
  }, [anchorEl, menuOpen, setMenuOpen]);

  return (
    <>
      <IconButton
        size="small"
        onClick={e => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        disabled={disabled}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableEnforceFocus
      >
        <MenuItem
          onClick={e => {
            e.stopPropagation();
            setAnchorEl(null);
            if (onRequestRename) onRequestRename();
          }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={e => {
            e.stopPropagation();
            onDelete(threadId);
            setAnchorEl(null);
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

export default function SideMenu({
  visible,
  threads,
  onNewChat,
  onSelectThread,
  onDeleteThread,
  onRenameThread,
  isAdmin,
  onLogout,
  onAdmin,
}) {
  const [editingId, setEditingId] = useState(null);
  const [pendingEditId, setPendingEditId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef(null);
  const { t } = useTranslation();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [error, setError] = useState(""); // Optional: UI error feedback

  useEffect(() => {
    if (pendingEditId && !openMenuId) {
      setEditingId(pendingEditId);
      setPendingEditId(null);
    }
  }, [pendingEditId, openMenuId]);

  useEffect(() => {
    if (editingId) {
      const thread = threads.find(t => t.id === editingId);
      setEditValue((thread && thread.title) || "");
      setTimeout(() => {
        if (inputRef.current) inputRef.current.select();
      }, 10);
    }
  }, [editingId, threads]);

  const startEditing = t => {
    if (openMenuId === t.id) {
      setPendingEditId(t.id);
    } else {
      setEditingId(t.id);
      setEditValue(t.title || "");
      setTimeout(() => {
        if (inputRef.current) inputRef.current.select();
      }, 10);
    }
  };

  // Updated: Always clear editing state, even on error
  const finishEditing = async (t) => {
    const trimmed = editValue.trim();
    setEditingId(null);
    setEditValue("");
    setError(""); // Clear any old error
    try {
      if (trimmed && trimmed !== t.title) {
        await onRenameThread(t.id, trimmed);
      }
    } catch (e) {
      setError(t('rename_failed', { defaultValue: "Rename failed" }) + ": " + (e.message || e));
      // Optionally show toast or snackbar
      // alert("Rename failed: " + (e.message || e));
    }
  };

  if (!visible) return null;

  const getLastDate = (t) => t.lastMessageTime || t.createdAt || null;
  const sortedThreads = [...threads].sort(
    (a, b) => new Date(getLastDate(b)) - new Date(getLastDate(a))
  );

  const thisWeek = sortedThreads.filter(t => isThisWeek(getLastDate(t)));
  const last30 = sortedThreads.filter(t => isLast30ButNotThisWeek(getLastDate(t)));
  const older = sortedThreads.filter(t => isOlderThan30(getLastDate(t)));

  const renderThreadGroup = (threadList) =>
    threadList.map((t) => (
      <div
        key={t.id}
        className="thread-entry"
        onClick={e => {
          if (!editingId) onSelectThread(t);
        }}
        style={{ cursor: editingId === t.id ? "text" : "pointer" }}
      >
        {editingId === t.id ? (
          <input
            ref={inputRef}
            value={editValue}
            autoFocus
            onChange={e => setEditValue(e.target.value)}
            onBlur={async () => await finishEditing(t)}
            onKeyDown={async e => {
              if (e.key === "Enter") {
                e.preventDefault();
                await finishEditing(t);
              } else if (e.key === "Escape") {
                setEditingId(null);
                setEditValue("");
                setError(""); // Optional: clear error on escape
              }
            }}
            className="edit-thread-input"
            style={{
              width: "80%",
              fontSize: "1em",
              borderRadius: 5,
              border: "1px solid #bcbcbc",
              padding: "2px 6px",
            }}
          />
        ) : (
          <span
            className="thread-text"
            onDoubleClick={e => {
              e.stopPropagation();
              startEditing(t);
            }}
            style={{ userSelect: "text" }}
          >
            {t.title || "New chat created"}
          </span>
        )}
        <ThreadMenu
          threadId={t.id}
          onDelete={onDeleteThread}
          disabled={editingId === t.id}
          onRequestRename={() => {
            setOpenMenuId(t.id);
            setTimeout(() => {
              setOpenMenuId(null);
              startEditing(t);
            }, 100);
          }}
          menuOpen={openMenuId === t.id}
          setMenuOpen={open => setOpenMenuId(open ? t.id : null)}
        />
      </div>
    ));

  return (
    <aside className="sidemenu">
      <button className="side-menu-button" onClick={onNewChat}>
        {t('new_chat')}
      </button>
      <h4 className="conversations"><span style={{ margin: "5px" }}>{t('conversations')}</span></h4>
      {error && (
        <div style={{ color: "#e53935", margin: "10px 8px 0 8px", fontSize: 13, fontWeight: 500 }}>
          {error}
        </div>
      )}
      <div className="conversation-section">
        {thisWeek.length > 0 && (
          <>
            <div style={{ margin: "12px 2px 4px 0", color: "#00ffc8", fontWeight: "600", fontSize: "15px" }}>
              <span style={{ margin: "5px" }}>{t('this_week')}</span>
            </div>
            {renderThreadGroup(thisWeek)}
          </>
        )}
        {last30.length > 0 && (
          <>
            <div style={{ margin: "16px 2px 4px 0", color: "#aaa", fontWeight: "500", fontSize: "14px" }}>
              <span style={{ margin: "5px" }}>{t('previous_30_days')}</span>
            </div>
            {renderThreadGroup(last30)}
          </>
        )}
        {older.length > 0 && (
          <>
            <div style={{ margin: "16px 0 4px 0", color: "#bbb", fontWeight: "500", fontSize: "14px" }}>
              <span style={{ margin: "5px" }}>{t('older')}</span>
            </div>
            {renderThreadGroup(older)}
          </>
        )}
        {thisWeek.length === 0 && last30.length === 0 && older.length === 0 && (
          <div style={{ margin: "24px 0", color: "#aaa" }}>
            <span style={{ margin: "5px" }}>{t('no_conversations_yet')}</span>
          </div>
        )}
      </div>
      <div className="bottom-container">
        <button className="logout-button" onClick={onLogout}>
          {t('logout')}
        </button>
        {isAdmin && (
          <button className="admin-button" onClick={onAdmin}>
            {t('admin')}
          </button>
        )}
      </div>
    </aside>
  );
}